import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AircraftData, CalculationsSummaryInputsAircraft } from '../../components/InputForm/types/ESALTypesAircraft';
import { RootState } from '../../../../reduxService';

/**
 * Redux state for Aircraft ESAL Calculation Modal
 */
export interface EsalStateAircraft {
    aircraft: AircraftData[];
    summaryInputs: CalculationsSummaryInputsAircraft;
}

/**
 * Initial state for Aircraft ESAL Calculation Modal
 */
export const initialStateAircraft: EsalStateAircraft = {
    aircraft: [],
    summaryInputs: {
        dd: 0.5,
        dl: 0.9,
        analysisPeriod: 12,
        growthRate: 2,
        trafficDesignLane: 0,
    },
};

/**
 * Redux slice for Aircraft ESAL Calculation Modal state
 */
export const esalSliceAircraft = createSlice({
    name: 'esalAircraft',
    initialState: initialStateAircraft,
    reducers: {
        /**
         * Set all aircraft data
         */
        setAircraft(state, action: PayloadAction<AircraftData[]>) {
            state.aircraft = action.payload;
        },
        /**
         * Update a single aircraft by id
         */
        updateAircraft(state, action: PayloadAction<AircraftData>) {
            const idx = state.aircraft.findIndex(a => a.id === action.payload.id);
            if (idx !== -1) {
                state.aircraft[idx] = action.payload;
            }
        },
        /**
         * Set all summary inputs with validation
         */
        setSummaryInputsAircraft(state, action: PayloadAction<CalculationsSummaryInputsAircraft>) {
            // Basic validation to ensure values are within reasonable ranges
            const { dd, dl, analysisPeriod, growthRate, trafficDesignLane } = action.payload;

            state.summaryInputs = {
                dd: Math.max(0, Math.min(1, dd)), // 0-1 range
                dl: Math.max(0, Math.min(1, dl)), // 0-1 range
                analysisPeriod: Math.max(1, Math.min(100, analysisPeriod)), // 1-100 years
                growthRate: Math.max(-10, Math.min(50, growthRate)), // -10% to 50%
                trafficDesignLane: Math.max(0, trafficDesignLane), // Non-negative
            };
        },
        /**
         * Reset Aircraft ESAL state to initial values
         */
        resetEsalAircraftState(state) {
            state.aircraft = [];
            state.summaryInputs = initialStateAircraft.summaryInputs;
        },
    },
});

/**
 * Selector for ESAL aircraft
 * Returns initialStateAircraft.aircraft if state.Dynamic.esalAircraft is undefined
 */
export const selectEsalAircraft = (state: RootState) => state.Dynamic?.esalAircraft?.aircraft ?? initialStateAircraft.aircraft;

/**
 * Selector for ESAL aircraft summary inputs
 * Returns initialStateAircraft.summaryInputs if state.Dynamic.esalAircraft is undefined
 */
export const selectEsalAircraftSummaryInputs = (state: RootState) => state.Dynamic?.esalAircraft?.summaryInputs ?? initialStateAircraft.summaryInputs;

export const { setAircraft, updateAircraft, setSummaryInputsAircraft, resetEsalAircraftState } = esalSliceAircraft.actions;
export default esalSliceAircraft.reducer;
